import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { addEditleads } from 'src/ApiServices';
import { toast } from 'react-toastify';
import useMediaQuery from '@mui/material/useMediaQuery';

const LeadModal = ({ open, onClose, onSuccess, lead }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    note: '',
    property_id: '',
    quiz_id: ''
  });
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        contact: lead.contact || '',
        note: lead.note || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        contact: '',
        note: ''
      });
    }
  }, [lead, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        lead_type_id: 1,
        id: lead?.id || undefined
      };

      await addEditleads(payload);
      toast.success(`Lead ${lead ? 'updated' : 'created'} successfully!`);
      onSuccess();
    } catch (error) {
      console.error('Failed to save lead:', error);
      toast.error(error.response?.data?.message || `Failed to ${lead ? 'update' : 'create'} lead!`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1000px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          position: 'relative'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText
          }}
        >
          {lead ? 'Edit Lead' : 'Add New Lead'}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: palette.secondary.contrastText
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label="Contact"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="note"
                value={formData.note}
                onChange={handleChange}
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: palette.grey[400],
              color: palette.text.primary,
              minWidth: 100
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
              minWidth: 100,
              '&:hover': {
                backgroundColor: palette.primary.dark
              }
            }}
            disabled={loading}
          >
            {loading ? 'Saving...' : lead ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LeadModal;
